import { Module } from '../enums/modules.enum';

export const ShipType = {
  Vessel: 'ship-type-vessel',
  Barge: 'ship-type-barge',
  OffshoreVessel: 'ship-type-offshore-vessel',
  OffshoreBarge: 'ship-type-offshore-barge',
} as const;

export const SHIP_TYPE_LABELS: Record<string, string> = {
  [ShipType.Vessel]: 'Vessel',
  [ShipType.Barge]: 'Barge',
  [ShipType.OffshoreVessel]: 'Offshore vessel',
  [ShipType.OffshoreBarge]: 'Offshore barge',
};

export const SHIP_TYPE_OPTIONS: { value: string; label: string }[] = [
  { value: ShipType.Vessel, label: 'Vessel' },
  { value: ShipType.Barge, label: 'Barge' },
  { value: ShipType.OffshoreVessel, label: 'Offshore vessel' },
  { value: ShipType.OffshoreBarge, label: 'Offshore barge' },
];

export enum ShipGroup {
  Vessel = 'vessel',
  Barge = 'barge',
}

/**
 * Returns the hull group (Vessel or Barge) for the given ship type.
 * Null, undefined, empty, or unrecognised values default to Vessel,
 * matching the backend's legacy default.
 */
export function determineShipGroup(shipType: string | null | undefined): ShipGroup {
  if (shipType === ShipType.Barge || shipType === ShipType.OffshoreBarge) {
    return ShipGroup.Barge;
  }
  return ShipGroup.Vessel;
}

export function isBargeType(shipType: string | null | undefined): boolean {
  return determineShipGroup(shipType) === ShipGroup.Barge;
}

export function isVesselType(shipType: string | null | undefined): boolean {
  return determineShipGroup(shipType) === ShipGroup.Vessel;
}

export function isOffshoreShip(shipType: string | null | undefined): boolean {
  return shipType === ShipType.OffshoreVessel || shipType === ShipType.OffshoreBarge;
}

export function isSingleDeckShip(shipType: string | null | undefined): boolean {
  return isBargeType(shipType) || isOffshoreShip(shipType);
}

/** Canonical ship-type tag; null, empty or unrecognised values fall back to Vessel (legacy default). */
export function normalizeShipType(shipType: string | null | undefined): string {
  return (Object.values(ShipType) as string[]).includes(shipType ?? '') ? (shipType as string) : ShipType.Vessel;
}

/**
 * Ship type of a user ship, wherever it is carried: detail endpoints put it on the nested ship,
 * the group explorer endpoint sets the flat shipType field instead.
 */
export function resolveShipType(
  userShip: { shipType?: string | null; ship?: { shipType?: string | null } | null } | null | undefined
): string | null {
  return userShip?.ship?.shipType ?? userShip?.shipType ?? null;
}

/**
 * Extra ship types a module covers inside the module itself, regardless of the user's other
 * subscriptions (mooring a barge without Barging). Requests from these modules carry the module
 * header (BaseApiService), so the server widens its scope to match on [ModuleShipScope] endpoints.
 * Mirrors the server's LicenseShipTypes.InModuleAccessMap.
 */
export const IN_MODULE_SHIP_TYPES: Partial<Record<Module, readonly string[]>> = {
  [Module.Mooring]: Object.values(ShipType),
};

/** Whether the URL segment names a module with in-module ship types; own keys only, so inherited names don't match. */
export function isInModuleScoped(segment: string | undefined): segment is Module {
  return segment !== undefined && Object.hasOwn(IN_MODULE_SHIP_TYPES, segment);
}

/**
 * Returns the set of ship types the given module is allowed to display in the
 * Getting Started picker.  For Lashing the allowed set expands based on the
 * user's optional Barging / Offshore subscriptions; modules in IN_MODULE_SHIP_TYPES
 * add their in-module types.
 */
export function allowedShipTypesForModule(module: Module, hasBarging: boolean, hasOffshore: boolean): Set<string> {
  const types = baseShipTypesForModule(module, hasBarging, hasOffshore);
  IN_MODULE_SHIP_TYPES[module]?.forEach(type => types.add(type));
  return types;
}

/** The ship types a module grants everywhere, without its in-module extras (as LicenseShipTypes.AccessMap). */
function baseShipTypesForModule(module: Module, hasBarging: boolean, hasOffshore: boolean): Set<string> {
  switch (module) {
    case Module.Stowage:
      return new Set([ShipType.Vessel]);
    case Module.Barging:
      return new Set([ShipType.Barge]);
    case Module.Offshore:
      return new Set([ShipType.OffshoreVessel, ShipType.OffshoreBarge]);
    case Module.CargoLoader:
      return new Set([ShipType.Vessel]);
    case Module.Mooring:
      return new Set([ShipType.Vessel]);
    case Module.Lashing: {
      const types = new Set<string>([ShipType.Vessel]);
      if (hasBarging) types.add(ShipType.Barge);
      if (hasOffshore) {
        types.add(ShipType.OffshoreVessel);
        types.add(ShipType.OffshoreBarge);
      }
      return types;
    }
    default:
      return new Set([ShipType.Vessel]);
  }
}

/**
 * The module whose subscription would grant access to a ship of this type - used to tell the
 * user which module a denial was about. Barges are granted by Barging, offshore ships by
 * Offshore; for plain vessels the granting module depends on the context (Lashing on a lashing
 * route, Stowage as the canonical fallback), so the caller passes it.
 */
export function requiredModuleForShipType(shipType: string | null | undefined, vesselModule: Module): Module {
  switch (normalizeShipType(shipType)) {
    case ShipType.Barge:
      return Module.Barging;
    case ShipType.OffshoreVessel:
    case ShipType.OffshoreBarge:
      return Module.Offshore;
    default:
      return vesselModule;
  }
}

/**
 * Union of ship types the given subscriptions may open — used to gate the vessel routes
 * (e.g. Barging-only users may open barges but no offshore vessels or offshore barges).
 * Uses the per-module base sets: IN_MODULE_SHIP_TYPES hold inside their own module only.
 */
export function allowedShipTypesForSubscriptions(subscriptions: Module[]): Set<string> {
  const hasBarging = subscriptions.includes(Module.Barging);
  const hasOffshore = subscriptions.includes(Module.Offshore);
  const allowed = new Set<string>();
  for (const module of subscriptions) {
    baseShipTypesForModule(module, hasBarging, hasOffshore).forEach(type => allowed.add(type));
  }
  return allowed;
}
